import { useFlag } from '@afterpaytouch/feature'
import { CheckoutStatus, Features, NotificationStatus } from '../../model'
import {
  setNotificationStatus,
  useAdjustedDownPaymentMessagingEnabled,
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useIsBillingAgreementApprovalProcess,
  useToastContext,
} from '../../state'
import { FirstPaymentType } from '@afterpaytouch/portal-api'
import { Theme, useIsTheme, useToast, useIsTouchScreen } from '@afterpaytouch/core'
import { useEffect } from 'react'
import { ToastMessage } from './ToastMessage'
import { useMoney } from '../Money/useMoney'

export const useShowPaymentScheduleToast = (): void => {
  const { status, started, firstPaymentType, notificationStatus } = useCheckoutState()
  const npufPaymentType = firstPaymentType === FirstPaymentType.NoPaymentUpfront
  const vpufOrLpufPaymentType = firstPaymentType === FirstPaymentType.VariablePaymentUpfront || firstPaymentType === FirstPaymentType.LargePaymentUpfront
  const autopayToggleIsEnabled = useFlag(Features.AUTOPAY_TOGGLE_ENABLED)
  const adjustedDownPaymentMessagingEnabled = useAdjustedDownPaymentMessagingEnabled()
  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()
  const isKelpieTheme = useIsTheme(Theme.Kelpie)
  const isKelpieMintTheme = useIsTheme(Theme.KelpieMint)
  const isKelpieOrKelpieMintTheme = isKelpieTheme || isKelpieMintTheme
  const shouldShowVpufToast = (autopayToggleIsEnabled || isKelpieOrKelpieMintTheme) && vpufOrLpufPaymentType
  const shouldShowToast = (shouldShowVpufToast || npufPaymentType) && !adjustedDownPaymentMessagingEnabled && !isBillingAgreementApprovalProcess
  const toast = useToast()
  const isTouchScreen = useIsTouchScreen()
  const toastContext = useToastContext()
  const dispatch = useAppDispatch()
  const isAPTheme = useIsTheme(Theme.AP)
  const { locale } = useAppRouter()
  const { formatMoney } = useMoney({ locale, currency: started?.installmentAmount.currency })
  const showToast = (): void => {
    const headingSize = shouldShowVpufToast ? 'XS' : 'L'
    dispatch(setNotificationStatus(NotificationStatus.Read))
    toast({
      message: () => <ToastMessage headingSize={headingSize} context={toastContext} amount={formatMoney(started?.amountDueToday?.amount)} />,
      kind: isAPTheme ? 'Success' : 'Default',
      toastTime: shouldShowVpufToast ? 0 : 5000,
      swipeToDismiss: isTouchScreen,
      testNameSpace: 'payment-schedule',
      manualDismiss: shouldShowVpufToast,
      ...(vpufOrLpufPaymentType ? { icon: 'InformationFill' } : {}),
    })
  }
  useEffect(() => {
    if (status === CheckoutStatus.Started && notificationStatus === NotificationStatus.Unread) {
      if (shouldShowToast) {
        showToast()
      }
    }
  }, [firstPaymentType, notificationStatus, autopayToggleIsEnabled, isBillingAgreementApprovalProcess])
}
